import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BrandLockup } from "../components/app/Logo";
import { BackButton } from "../components/app/BackButton";
import { PasswordInput } from "../components/app/PasswordInput";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const [params] = useSearchParams();
  const initial = params.get("mode") === "signup" ? "signup" : "login";
  const [tab, setTab] = useState(initial);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [authError, setAuthError] = useState("");
  const emailRef = useRef(null);
  const { loginWithCredentials, registerWithCredentials, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const emailInvalid = emailTouched && email.length > 0 && !EMAIL_RE.test(email);
  const emailValid = emailTouched && EMAIL_RE.test(email);

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    // autofocus email on mount
    emailRef.current?.focus();
  }, [tab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (!EMAIL_RE.test(email)) { setEmailTouched(true); return; }
    setBusy(true);
    try {
      await loginWithCredentials(email, password);
      toast.success(t("toast_welcome"));
      navigate("/dashboard");
    } catch (err) {
      setAuthError(t("login_error_generic"));
    } finally {
      setBusy(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthError("");
    if (!EMAIL_RE.test(email)) { setEmailTouched(true); return; }
    setBusy(true);
    try {
      await registerWithCredentials(name, email, password);
      toast.success(t("toast_account_created"));
      navigate("/dashboard");
    } catch (err) {
      setAuthError(err?.response?.data?.detail || t("toast_signup_failed"));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      <div className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-6 md:px-8 h-14 flex items-center justify-between">
          <Link to="/" data-testid="back-home-link">
            <BrandLockup />
          </Link>
          <BackButton fallback="/" />
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between border-r border-white/10 p-12 relative overflow-hidden">
          <div className="absolute inset-0 terminal-grid opacity-40 pointer-events-none" />
          <div className="relative">
            <span className="label-mono">{t("login_session")}</span>
            <h1 className="mt-4 text-4xl font-semibold tracking-tighter leading-tight">
              {t("login_title_1")}<br />{t("login_title_2")}<span className="text-bull">_</span>
            </h1>
            <p className="mt-6 text-neutral-400 max-w-md">{t("login_desc")}</p>
          </div>
          <div className="relative grid grid-cols-2 gap-3 max-w-md">
            <div className="surface-card p-4">
              <span className="label-mono">UPTIME</span>
              <div className="font-mono text-xl mt-1 text-bull">99.97%</div>
            </div>
            <div className="surface-card p-4">
              <span className="label-mono">MODEL</span>
              <div className="font-mono text-xl mt-1">sonnet-4.5</div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 md:p-12">
          <div className="w-full max-w-md">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-2 w-full bg-transparent border border-white/10 rounded-none h-11 p-0">
                <TabsTrigger value="login" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black font-mono text-xs tracking-[0.2em] uppercase" data-testid="tab-login">
                  {t("tab_login")}
                </TabsTrigger>
                <TabsTrigger value="signup" className="rounded-none data-[state=active]:bg-white data-[state=active]:text-black font-mono text-xs tracking-[0.2em] uppercase" data-testid="tab-signup">
                  {t("tab_signup")}
                </TabsTrigger>
              </TabsList>

              <Button
                type="button"
                onClick={handleGoogle}
                variant="outline"
                className="mt-6 w-full h-11 rounded-none bg-transparent border-white/15 hover:bg-white/5 text-white justify-center gap-3"
                data-testid="google-login-btn"
              >
                <svg className="h-4 w-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 3l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 3l5.7-5.7C33.9 7 29.2 5 24 5 16.3 5 9.7 9.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-2 13.2-5.2l-6.1-5.2C29 34.5 26.6 35.5 24 35.5c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.6 39.1 16.2 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4-3.9 5.3l6.1 5.2C40.3 36 43.5 30.5 43.5 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
                <span className="font-medium">{t("google_btn")}</span>
              </Button>

              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 ticker-divider" />
                <span className="label-mono">{t("or_divider")}</span>
                <div className="flex-1 ticker-divider" />
              </div>

              <TabsContent value="login" className="mt-0 animate-fade-up">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label className="label-mono">{t("field_email")}</Label>
                    <Input ref={emailRef} type="email" required value={email}
                      onChange={(e) => { setEmail(e.target.value); setAuthError(""); }}
                      onBlur={() => setEmailTouched(true)}
                      autoFocus
                      className={`bg-[#0e0e0e] rounded-none h-11 font-mono transition-colors duration-200 focus-visible:ring-bull/40 ${emailInvalid ? "border-bear focus-visible:border-bear" : emailValid ? "border-bull/60 focus-visible:border-bull" : "border-white/10 focus-visible:border-bull/40"}`}
                      placeholder={t("placeholder_email")}
                      data-testid="login-email-input" />
                    {emailInvalid && (
                      <p className="font-mono text-[11px] text-bear flex items-center gap-1" data-testid="login-email-error">
                        <AlertCircle className="h-3 w-3" strokeWidth={2} /> {t("login_email_invalid")}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="label-mono">{t("field_password")}</Label>
                    <PasswordInput required value={password} onChange={(e) => { setPassword(e.target.value); setAuthError(""); }}
                      className="bg-[#0e0e0e] border-white/10 rounded-none h-11 font-mono focus-visible:ring-bull/40 focus-visible:border-bull/40"
                      placeholder={t("placeholder_password")}
                      data-testid="login-password-input" />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full h-11 rounded-none bg-white text-black hover:bg-neutral-200 hover:-translate-y-px transition-transform font-medium" data-testid="login-submit-btn">
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("login_loading")}</> : t("btn_authenticate")}
                  </Button>
                  {authError && (
                    <p className="font-mono text-xs text-bear flex items-center gap-1.5 justify-center" data-testid="login-auth-error">
                      <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} /> {authError}
                    </p>
                  )}
                  <Link to="/forgot-password" className="block text-center font-mono text-[11px] text-neutral-500 hover:text-bull tracking-wider transition-colors" data-testid="forgot-password-link">
                    {t("forgot_link")}
                  </Link>
                </form>
              </TabsContent>

              <TabsContent value="signup" className="mt-0 animate-fade-up">
                <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
                  <div className="space-y-2">
                    <Label className="label-mono">{t("field_name")}</Label>
                    <Input required value={name} onChange={(e) => setName(e.target.value)}
                      className="bg-[#0e0e0e] border-white/10 rounded-none h-11 font-mono focus-visible:ring-bull/40 focus-visible:border-bull/40"
                      placeholder={t("placeholder_name")}
                      data-testid="signup-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="label-mono">{t("field_email")}</Label>
                    <Input type="email" required value={email}
                      onChange={(e) => { setEmail(e.target.value); setAuthError(""); }}
                      onBlur={() => setEmailTouched(true)}
                      className={`bg-[#0e0e0e] rounded-none h-11 font-mono transition-colors duration-200 focus-visible:ring-bull/40 ${emailInvalid ? "border-bear focus-visible:border-bear" : emailValid ? "border-bull/60 focus-visible:border-bull" : "border-white/10 focus-visible:border-bull/40"}`}
                      placeholder={t("placeholder_email")}
                      data-testid="signup-email-input" />
                    {emailInvalid && (
                      <p className="font-mono text-[11px] text-bear flex items-center gap-1" data-testid="signup-email-error">
                        <AlertCircle className="h-3 w-3" strokeWidth={2} /> {t("login_email_invalid")}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="label-mono">{t("field_password")}</Label>
                    <PasswordInput required minLength={6} value={password} onChange={(e) => { setPassword(e.target.value); setAuthError(""); }}
                      className="bg-[#0e0e0e] border-white/10 rounded-none h-11 font-mono focus-visible:ring-bull/40 focus-visible:border-bull/40"
                      placeholder={t("placeholder_password_signup")}
                      data-testid="signup-password-input" />
                  </div>
                  <Button type="submit" disabled={busy} className="w-full h-11 rounded-none bg-white text-black hover:bg-neutral-200 hover:-translate-y-px transition-transform font-medium" data-testid="signup-submit-btn">
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t("signup_loading")}</> : t("btn_create_account")}
                  </Button>
                  {authError && (
                    <p className="font-mono text-xs text-bear flex items-center gap-1.5 justify-center" data-testid="signup-auth-error">
                      <AlertCircle className="h-3.5 w-3.5" strokeWidth={2} /> {authError}
                    </p>
                  )}
                </form>
              </TabsContent>
            </Tabs>

            <p className="mt-8 font-mono text-[11px] tracking-wider text-neutral-500 leading-relaxed">
              {t("login_legal")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
